// Copyright (c) Microsoft. All rights reserved.

import { Button, Spinner, Textarea, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { AttachRegular, SendPersonRegular } from '@fluentui/react-icons';
import React, { useRef, useState } from 'react';
import { Constants } from '../../Constants';
import ghsLogo from '../../ghs-logo-circle.svg';
import { useChat } from '../../libs/hooks/useChat';
import { useFile } from '../../libs/hooks/useFile';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';

const GENESIS_GREEN = '#00583f';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        backgroundColor: tokens.colorNeutralBackground3,
        position: 'relative',
        ...shorthands.overflow('hidden'),
    },
    accentBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        backgroundColor: GENESIS_GREEN,
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingVerticalL),
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRadius(tokens.borderRadiusXLarge),
        boxShadow: tokens.shadow16,
        ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXXL),
        width: '100%',
        maxWidth: '640px',
        ...shorthands.margin(tokens.spacingVerticalNone, tokens.spacingHorizontalM),
    },
    logoRow: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    logo: {
        height: '40px',
        width: '40px',
    },
    appName: {
        fontSize: tokens.fontSizeBase600,
        fontWeight: tokens.fontWeightSemibold,
        color: GENESIS_GREEN,
    },
    divider: {
        width: '100%',
        height: '1px',
        backgroundColor: tokens.colorNeutralStroke2,
    },
    greeting: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground2,
        textAlign: 'left',
        lineHeight: tokens.lineHeightBase400,
    },
    inputRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        width: '100%',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    textarea: {
        flexGrow: 1,
    },
    hint: {
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground4,
        alignSelf: 'flex-end',
    },
    dNone: {
        display: 'none',
    },
});

export const NewChatLanding: React.FC = () => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const chat = useChat();
    const fileHandler = useFile();
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const { serviceInfo } = useAppSelector((state: RootState) => state.app);

    const [value, setValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const documentFileRef = useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        textAreaRef.current?.focus();
    }, []);

    const handleSubmit = async () => {
        const trimmed = value.trim();
        if (!trimmed || isSubmitting) return;

        setIsSubmitting(true);
        setValue('');

        try {
            const chatId = await chat.createAgentChat();
            if (!chatId) {
                setIsSubmitting(false);
                return;
            }

            // threadId may be set by the backend when the agent chat is created
            const threadId = conversations[chatId]?.threadId ?? ''; // eslint-disable-line @typescript-eslint/no-unnecessary-condition

            await chat.getAgentResponse({
                value: trimmed,
                messageType: ChatMessageType.Message,
                chatId,
                isAgent: true,
                threadId,
            });
        } catch (e: any) {
            dispatch(
                addAlert({
                    message: `Unable to start new session. Details: ${e instanceof Error ? e.message : String(e)}`,
                    type: AlertType.Error,
                }),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSubmit();
        }
    };

    const handleFileAttach = async () => {
        // Capture files BEFORE any awaits — the component will unmount when
        // createAgentChat() dispatches addAgentConversation (selectedId changes),
        // which nulls out documentFileRef.current.
        const filesArray = documentFileRef.current?.files ? Array.from(documentFileRef.current.files) : [];
        if (filesArray.length === 0) return;

        setIsSubmitting(true);
        try {
            // Create the session so we have a chatId to import into.
            const chatId = await chat.createAgentChat();
            if (!chatId) return;

            // Pass the pre-captured files directly — ref is null after unmount.
            const dt = new DataTransfer();
            filesArray.forEach((f) => dt.items.add(f));
            await fileHandler.handleImport(chatId, documentFileRef, false, undefined, dt.files);

            // Reload chat history after import so the file-upload message stored by the
            // backend is visible — SignalR group join may not have completed in time to
            // receive the push notification.
            await chat.loadFullChatHistory(chatId);
        } catch (e: any) {
            dispatch(
                addAlert({
                    message: `Unable to attach file. Details: ${e instanceof Error ? e.message : String(e)}`,
                    type: AlertType.Error,
                }),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={classes.root}>
            <div className={classes.accentBar} />
            <div className={classes.card}>
                <div className={classes.logoRow}>
                    <img src={ghsLogo} className={classes.logo} alt="GHS Logo" />
                    <span className={classes.appName}>GenGPT</span>
                </div>
                {serviceInfo.initialBotMessage && (
                    <>
                        <div className={classes.divider} />
                        <span className={classes.greeting}>{serviceInfo.initialBotMessage}</span>
                    </>
                )}
                <div className={classes.divider} />
                <div className={classes.inputRow}>
                    <input
                        type="file"
                        ref={documentFileRef}
                        className={classes.dNone}
                        accept={Constants.app.importTypes}
                        multiple={true}
                        onChange={() => void handleFileAttach()}
                    />
                    <Textarea
                        className={classes.textarea}
                        ref={textAreaRef}
                        value={value}
                        placeholder="Ask anything..."
                        resize="vertical"
                        disabled={isSubmitting}
                        onChange={(_e, data) => {
                            setValue(data.value);
                        }}
                        onKeyDown={handleKeyDown}
                        aria-label="New chat message input"
                    />
                    <Button
                        appearance="transparent"
                        icon={<AttachRegular />}
                        onClick={() => documentFileRef.current?.click()}
                        disabled={isSubmitting}
                        title="Attach file"
                        aria-label="Attach file"
                    />
                    {isSubmitting ? (
                        <Spinner size="small" />
                    ) : (
                        <Button
                            appearance="primary"
                            icon={<SendPersonRegular />}
                            onClick={() => void handleSubmit()}
                            disabled={!value.trim()}
                            aria-label="Start new session"
                            title="Start new session"
                        />
                    )}
                </div>
                <span className={classes.hint}>Press Enter to send · Shift+Enter for a new line</span>
            </div>
        </div>
    );
};
